import { Badge } from "@spz/ui/components/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@spz/ui/components/tooltip";
import { cn } from "@spz/ui/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { Typography } from "./typography";

const priorityBadgeVariants = cva("inline-flex items-center text-gray-900", {
  variants: {
    priority: {
      critical: "",
      high: "",
      medium: "",
      low: "",
      none: "",
    },
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const dotVariants = cva("rounded-full", {
  variants: {
    priority: {
      critical: "bg-red-200",
      high: "bg-orange-200",
      medium: "bg-yellow-300",
      low: "bg-gray-300",
      none: "hidden",
    },
    size: {
      sm: "w-[3px] h-[3px]",
      md: "w-1 h-1",
      lg: "w-1.5 h-1.5",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const badgeContainerVariants = cva(
  "bg-gray-50 rounded-custom border-none text-gray-900 ",
  {
    variants: {
      size: {
        sm: "text-[10px] w-5.5 h-5 gap-1",
        md: "text-[10px] w-7 h-5.5 gap-1.5",
        lg: "text-xs w-8",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

type PriorityVariant = "critical" | "high" | "medium" | "low" | "none";

const PRIORITY_VARIANTS: Record<string, PriorityVariant | undefined> = {
  critical: "critical",
  high: "high",
  medium: "medium",
  low: "low",
  none: "none",
};

const getVariantFromPriority = (priority: string): PriorityVariant => {
  return PRIORITY_VARIANTS[priority] ?? "none";
};

interface PriorityBadgeProps
  extends Omit<VariantProps<typeof priorityBadgeVariants>, "priority"> {
  id: string;
  variant: string;
  symbol: string;
  label: string;
  disabled?: boolean;
  showLabel?: boolean;
  className?: string;
}

export const PriorityBadge = ({
  id,
  variant = "none",
  symbol,
  label,
  disabled = false,
  size = "sm",
  showLabel = false,
  className,
}: PriorityBadgeProps) => {
  const { t } = useTranslation();
  const priorityVariant = getVariantFromPriority(variant);

  const badge = (
    <div
      className={cn(
        priorityBadgeVariants({
          size,
          priority: priorityVariant,
        }),
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <Badge className={cn(badgeContainerVariants({ size }), className)}>
        <span
          className={dotVariants({
            priority: priorityVariant,
            size,
          })}
          aria-hidden="true"
        />
        <Typography>{t(symbol)}</Typography>
      </Badge>
      {showLabel && <Typography className="px-2 font-semibold" trans={label} />}
    </div>
  );

  if (disabled || showLabel) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>
        <Typography trans={label} />
      </TooltipContent>
    </Tooltip>
  );
};
